import { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Button, Flex, Tooltip } from "antd"
import { RightOutlined, FilePdfOutlined } from "@ant-design/icons"
import { DollarSign } from "ui/Icon/DSIcon/DollarSign"
import DataTable from "ui/DataTable"
import { getHeaders } from "./headers"
import { useSelector } from "react-redux"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import CustomPagination from "components/common/CustomPagination"
import CancelBookingModal from "../BookingActions/CancelBookingModal"
import { routes } from "router"
import ActionQueue from "./ActionQueue"
import CrewCell from "./CrewCell"
import ResourceCell from "./ResourceCell"
import TicketTypeIcon from "./TicketTypeIcon"
import ReservationListingTable from "./ReservationListingTable"
import BookingTag from "../BookingTag"
import { PAYMENT_COLOR, PAYMENT_LABEL, DOLLAR_STROKE } from "./constants"
import styles from "./BookingTable.module.css"

const PaymentCell = ({ paymentStatus }) => {
  const color = PAYMENT_COLOR[paymentStatus] ?? "error"
  const label = PAYMENT_LABEL[paymentStatus] ?? "Payment Failed"
  return (
    <Tooltip title={label}>
      <span>
        <BookingTag color={color} className={styles.paymentTag}>
          <DollarSign stroke={DOLLAR_STROKE[color]} />
        </BookingTag>
      </span>
    </Tooltip>
  )
}

PaymentCell.propTypes = {
  paymentStatus: PropTypes.string
}

const DocsCell = ({ sign, total }) => (
  <BookingTag color={sign === total ? "success" : "error"}>
    <Flex align="center" gap={4}>
      <FilePdfOutlined />
      {sign}/{total}
    </Flex>
  </BookingTag>
)

DocsCell.propTypes = {
  sign: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
}

const ViewButton = ({ uuid, navigate }) => (
  <Button
    type="link"
    size="small"
    onClick={(e) => {
      e.stopPropagation()
      navigate(routes.guideBookingDetailsPath({ id: uuid }))
    }}
  >
    View
  </Button>
)

ViewButton.propTypes = {
  uuid: PropTypes.string,
  navigate: PropTypes.func.isRequired
}

const renderExpandedRow = (record) => <ActionQueue groupId={record._raw?.groupId || record._raw?.uuid} />

const ExpandIcon = ({ expanded, onExpand, record }) => (
  <RightOutlined className={expanded ? styles.expandIconOpen : styles.expandIcon} onClick={(e) => onExpand(record, e)} />
)

ExpandIcon.propTypes = {
  expanded: PropTypes.bool,
  onExpand: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired
}

const BookingTable = ({
  type = "booked",
  reservationStatus,
  page = 1,
  pageSize = 10,
  sortKey,
  sortOrder,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  selectedRowKeys,
  setSelectedRowKeys,
  fetchBookings,
  onOpenFilters,
  reservationFilters,
  onReservationFiltersChange,
  experienceOptions = []
}) => {
  const reservationsStore = useSelector((store) => store.reservations)
  const upcomingStore = useSelector((store) => store.upcomingExperiences)
  const { items, metadata, loading, firstLoaded } = type === "reservation" ? reservationsStore : upcomingStore
  // Only show the in-table spinner for refetches (tab switch, filter, sort, paginate).
  // The very first load is already covered by the page-level <FirstLoading> overlay, so
  // gating on firstLoaded prevents two spinners showing at once.
  const tableLoading = loading && firstLoaded
  const breakpoint = useBreakpoints()
  const isTabletOrMobile = ["xs", "xsm", "sm", "md", "lg"].includes(breakpoint)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelBooking] = useState(null)
  const navigate = useNavigate()
  const [expandedRowKeys, setExpandedRowKeys] = useState([])

  // Upcoming Experiences table — items are already mapped by the store
  const dataSource = useMemo(() => {
    return items.map((item) => ({
      key: item.uuid,
      booking_id: (
        <button
          type="button"
          className={styles.bookingIdCell}
          onClick={() => navigate(routes.guideBookingDetailsPath({ id: item.uuid }))}
        >
          <TicketTypeIcon isGroup={item.experienceIsGroup} />
          <span className={styles.bookingIdLink}>{item.bookingId}</span>
        </button>
      ),
      experience: <span className={styles.experienceText} title={item.tripTitle}>{item.tripTitle}</span>,
      booked_date: (
        <div className={styles.dateTimeCell}>
          <span className={styles.dateText}>{item.experienceDate || ""}</span>
          {item.experienceTime && <span className={styles.timeText}>{item.experienceTime}</span>}
        </div>
      ),
      guests: item.totalGuestCount,
      payment: <PaymentCell paymentStatus={item.paymentStatus} />,
      docs: item.isWaiverAttached ? (
        <DocsCell sign={item.signedWaiverCount || 0} total={item.totalGuestCount || 0} />
      ) : null,
      crew: <CrewCell initialCrew={item.crew || []} bookingId={item.tripBookingUuid} />,
      resources: <ResourceCell initialResources={item.resources || []} bookingId={item.tripBookingUuid} />,
      actions: <ViewButton uuid={item.uuid} navigate={navigate} />,
      _raw: item
    }))
  }, [items, navigate])

  // Reservation table — items are already camelCase-mapped by the reservations store
  const reservationDataSource = useMemo(() => items, [items])

  const handleExpand = (expanded, record) => {
    setExpandedRowKeys(expanded ? [record.key] : [])
  }

  if (type === "reservation") {
    return (
      <ReservationListingTable
        reservations={reservationDataSource}
        loading={tableLoading}
        status={reservationStatus}
        onRefresh={() => fetchBookings?.(false)}
        metadata={metadata}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
        onViewBooking={(reservation) => navigate(routes.guideReservationDetailPath({ id: reservation.id }))}
        onOpenFilters={onOpenFilters}
        filters={reservationFilters}
        onFiltersChange={onReservationFiltersChange}
        experienceOptions={experienceOptions}
      />
    )
  }

  return (
    <>
      <DataTable
        columns={getHeaders({ field: sortKey, order: sortOrder, type }).filter(
          (col) => !isTabletOrMobile || !["crew", "resources"].includes(col.key)
        )}
        dataSource={dataSource}
        loading={tableLoading}
        pagination={false}
        onChange={onSortChange}
        rowSelection={false}
        rowKey={(record) => record.key}
        rowClassName={(record) => (expandedRowKeys.includes(record.key) ? styles.rowExpanded : "")}
        sticky
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: renderExpandedRow,
          expandedRowKeys,
          onExpand: handleExpand,
          rowExpandable: () => true,
          expandIcon: ExpandIcon
        }}
      />
      <div className="mb-m">
        <CustomPagination
          metadata={metadata}
          pageSize={pageSize}
          total={metadata?.total_results || 0}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
      {cancelModalOpen && cancelBooking && (
        <CancelBookingModal
          open={cancelModalOpen}
          bookings={[cancelBooking]}
          onConfirm={() => {
            setCancelModalOpen(false)
            fetchBookings(false)
          }}
          onCancel={() => setCancelModalOpen(false)}
        />
      )}
    </>
  )
}

BookingTable.propTypes = {
  type: PropTypes.string,
  reservationStatus: PropTypes.string,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.string,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  selectedRowKeys: PropTypes.array.isRequired,
  setSelectedRowKeys: PropTypes.func.isRequired,
  fetchBookings: PropTypes.func,
  onOpenFilters: PropTypes.shape({ current: PropTypes.func }),
  reservationFilters: PropTypes.object,
  onReservationFiltersChange: PropTypes.func,
  experienceOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }))
}

export default BookingTable
